import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getFlag, getFlagSource, setFlagStatus } from './anomalyApi';

const s = {
    page:   { padding: 24, background: '#f8fafc', minHeight: '100vh', fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif' },
    back:   { color: '#2563eb', textDecoration: 'none', fontSize: 13, fontWeight: 600 },
    h1:     { fontSize: 20, fontWeight: 700, color: '#0f172a', marginTop: 12 },
    sub:    { fontSize: 13, color: '#64748b' },
    card:   { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginTop: 16 },
    label:  { fontSize: 11, textTransform: 'uppercase', color: '#64748b', letterSpacing: '.04em' },
    value:  { fontSize: 14, color: '#0f172a', marginTop: 2 },
    grid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 },
    pre:    { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 12, fontSize: 12, color: '#334155', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 320 },
    pill:   (sev) => ({
        display: 'inline-block', padding: '2px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700,
        background: sev === 'high' ? '#fee2e2' : sev === 'medium' ? '#fef3c7' : '#e0f2fe',
        color:      sev === 'high' ? '#991b1b' : sev === 'medium' ? '#92400e' : '#075985',
    }),
    btnRow: { display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' },
    btn:    (kind) => {
        const palette = {
            resolve:   { bg: '#16a34a', fg: '#fff' },
            dismiss:   { bg: '#fff',    fg: '#334155' },
            escalate:  { bg: '#dc2626', fg: '#fff' },
            reopen:    { bg: '#0f172a', fg: '#fff' },
        }[kind] || { bg: '#fff', fg: '#0f172a' };
        return {
            padding: '8px 14px', borderRadius: 6, border: '1px solid #e2e8f0',
            background: palette.bg, color: palette.fg, fontSize: 13, fontWeight: 600,
            cursor: 'pointer',
        };
    },
    textarea: { width: '100%', minHeight: 80, padding: 10, fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit', resize: 'vertical' },
    err:    { padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginTop: 12 },
};

export default function FlagDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [flag, setFlag] = useState(null);
    const [source, setSource] = useState(null);
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [f, src] = await Promise.all([getFlag(id), getFlagSource(id)]);
                if (cancelled) return;
                setFlag(f.data);
                setSource(src.data);
                setNote(f.data?.manager_note || '');
            } catch (err) {
                if (!cancelled) setError(err.message);
            }
        })();
        return () => { cancelled = true; };
    }, [id]);

    async function handle(action) {
        setBusy(true);
        setError(null);
        try {
            await setFlagStatus(id, { status: action, manager_note: note || null });
            navigate('/anomaly/flags');
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    }

    if (error && !flag) return <div style={s.page}><div style={s.err}>Error: {error}</div></div>;
    if (!flag) return <div style={s.page}><div style={s.sub}>Loading…</div></div>;

    let ctx = {};
    try { ctx = flag.computed_context ? JSON.parse(flag.computed_context) : {}; } catch {}

    return (
        <div style={s.page}>
            <Link to="/anomaly/flags" style={s.back}>← back to flags</Link>
            <h1 style={s.h1}>
                <span style={s.pill(flag.severity)}>{flag.severity.toUpperCase()}</span>
                {'  '}<code style={{ fontSize: 16 }}>{flag.rule_id}</code>
            </h1>
            <div style={s.sub}>{flag.computed_reason}</div>

            <div style={s.card}>
                <div style={s.grid}>
                    <Field label="Status"     value={flag.status} />
                    <Field label="Source"     value={flag.source_type} />
                    <Field label="Employee"   value={flag.cardholder_or_employee_id} />
                    <Field label="Project"    value={flag.project_id} />
                    <Field label="Amount"     value={flag.flag_amount_usd != null ? `$${Number(flag.flag_amount_usd).toFixed(2)}` : '—'} />
                    <Field label="Manager"    value={flag.manager_employee_id || '—'} />
                    <Field label="Created"    value={flag.created_at_utc ? new Date(flag.created_at_utc).toLocaleString() : '—'} />
                    <Field label="Rule ver."  value={flag.rule_version} />
                </div>
            </div>

            {flag.narrative && (
                <div style={s.card}>
                    <div style={s.label}>Narrative</div>
                    <div style={{ ...s.value, marginTop: 6, lineHeight: 1.5 }}>{flag.narrative}</div>
                </div>
            )}

            <div style={s.card}>
                <div style={s.label}>Computed context (rule-specific)</div>
                <pre style={s.pre}>{JSON.stringify(ctx, null, 2)}</pre>
            </div>

            {source && (
                <div style={s.card}>
                    <div style={s.label}>Source enriched row</div>
                    <pre style={s.pre}>{JSON.stringify(source, null, 2)}</pre>
                </div>
            )}

            <div style={s.card}>
                <div style={s.label}>Manager note</div>
                <textarea style={s.textarea} value={note} onChange={e => setNote(e.target.value)}
                          placeholder="What did you find when you investigated? (optional)" />
                <div style={s.btnRow}>
                    <button style={s.btn('resolve')}  onClick={() => handle('resolved')}  disabled={busy}>Resolve</button>
                    <button style={s.btn('dismiss')}  onClick={() => handle('dismissed')} disabled={busy}>Dismiss</button>
                    <button style={s.btn('escalate')} onClick={() => handle('escalated')} disabled={busy}>Escalate</button>
                    {flag.status !== 'open' && (
                        <button style={s.btn('reopen')} onClick={() => handle('open')} disabled={busy}>Reopen</button>
                    )}
                </div>
                {error && <div style={s.err}>Error: {error}</div>}
            </div>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div style={s.label}>{label}</div>
            <div style={s.value}>{value || '—'}</div>
        </div>
    );
}
